import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { X, Loader2 } from 'lucide-react';
import { useHelp } from '../../contexts/HelpContext';

interface HelpModalProps {
  isOpen: boolean;
  onClose: () => void;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  theme?: 'light' | 'dark';
}

export const HelpModal: React.FC<Partial<HelpModalProps>> = ({
  size = 'lg',
  theme = 'light'
} = {}) => {
  const { isOpen, currentGuide, closeHelp, preferences, markGuideAsSeen } = useHelp();
  const [content, setContent] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const sizeClasses = {
    sm: 'max-w-md',
    md: 'max-w-2xl',
    lg: 'max-w-4xl',
    xl: 'max-w-6xl'
  };

  useEffect(() => {
    if (isOpen && currentGuide) {
      loadGuideContent(currentGuide);
    }
  }, [isOpen, currentGuide]);

  const loadGuideContent = async (guideId: string) => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`/guides/${guideId}.md`);
      if (!response.ok) {
        throw new Error('Guide not found');
      }
      const text = await response.text();
      setContent(text);

      // Mark as seen
      markGuideAsSeen(guideId);
    } catch (err) {
      setError('Failed to load help content');
      console.error('Error loading guide:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    closeHelp();
  };

  if (!isOpen) return null;

  const modalTheme = theme === 'dark' || preferences.theme === 'dark' ? 'dark' : 'light';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className={`bg-white dark:bg-dark-bg-primary rounded-xl shadow-xl ${sizeClasses[size]} w-full max-h-[90vh] overflow-hidden flex flex-col`}>
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-dark-border-primary">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-dark-text-primary flex items-center">
            Help Guide
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-dark-text-secondary transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading && (
            <div className="flex items-center justify-center h-32">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
              <span className="ml-2 text-gray-600 dark:text-dark-text-secondary">Loading help content...</span>
            </div>
          )}

          {error && (
            <div className="text-center py-8">
              <p className="text-red-600 dark:text-red-400 mb-4">{error}</p>
              <button
                onClick={() => currentGuide && loadGuideContent(currentGuide)}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg"
              >
                Retry
              </button>
            </div>
          )}

          {!loading && !error && content && (
            <div className={`prose prose-sm sm:prose lg:prose-lg xl:prose-xl max-w-none ${
              modalTheme === 'dark'
                ? 'prose-invert prose-headings:text-dark-text-primary prose-p:text-dark-text-secondary prose-strong:text-dark-text-primary prose-code:bg-gray-800 prose-code:text-gray-100'
                : 'prose-headings:text-gray-900 prose-p:text-gray-700 prose-code:bg-gray-100 prose-code:text-gray-900'
            }`}>
              <ReactMarkdown
                components={{
                  h1: ({ children }) => <h1 className="text-2xl font-bold mb-4">{children}</h1>,
                  h2: ({ children }) => <h2 className="text-xl font-semibold mb-3">{children}</h2>,
                  h3: ({ children }) => <h3 className="text-lg font-medium mb-2">{children}</h3>,
                  p: ({ children }) => <p className="mb-3 leading-relaxed">{children}</p>,
                  ul: ({ children }) => <ul className="list-disc list-inside mb-3 space-y-1">{children}</ul>,
                  ol: ({ children }) => <ol className="list-decimal list-inside mb-3 space-y-1">{children}</ol>,
                  li: ({ children }) => <li className="text-sm">{children}</li>,
                  code: ({ children, className }) => {
                    const isInline = !className;
                    return isInline
                      ? <code className="bg-gray-100 dark:bg-gray-800 px-1 py-0.5 rounded text-sm">{children}</code>
                      : <pre className="bg-gray-50 dark:bg-gray-900 p-4 rounded-lg overflow-x-auto"><code>{children}</code></pre>;
                  },
                  strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
                  em: ({ children }) => <em className="italic">{children}</em>,
                  a: ({ children, href }) => (
                    <a
                      href={href}
                      className="text-blue-600 dark:text-blue-400 hover:underline"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {children}
                    </a>
                  )
                }}
              >
                {content}
              </ReactMarkdown>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end space-x-3 p-6 border-t border-gray-200 dark:border-dark-border-primary">
          <button
            onClick={handleClose}
            className="bg-gray-100 dark:bg-dark-bg-tertiary hover:bg-gray-200 dark:hover:bg-dark-hover-bg text-gray-800 dark:text-dark-text-primary py-2 px-4 rounded-lg transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};